import { useCallback, useMemo, useRef, type ComponentType } from 'react';
import type { PaginationChangedEvent, SelectionChangedEvent } from 'ag-grid-community';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import type { AgGridFetchFunction, AgGridRowData } from '@/app/common/components/ag-grid/types';
import type { useFetchProjectPromptListPageMutation } from '../../api/trackerEndpointsApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { promptsColumnConfig } from '../../config/promptsColumnConfig';
import type { ProjectPrompt } from '../../hooks/useProjectData';
import { toGridFetchFunction } from '../../utils/grid-fetch';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  fetchFunction?: AgGridFetchFunction;
  additionalData?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  otherOptions?: object;
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

type FetchPromptPage = ReturnType<typeof useFetchProjectPromptListPageMutation>[0];

export function PromptsTable({
  prompts,
  total,
  fetchPage,
  additionalData,
  selectedId,
  runningId,
  runningIds,
  showTopic,
  loading,
  height = '620px',
  onSelect,
  onSelectionChange,
  onRun,
  onStop,
}: {
  /** All rows (client mode), or page 1 when `fetchPage` is set (server mode). */
  prompts: ProjectPrompt[];
  total?: number;
  /** `POST /projects/:id/prompts/list` page trigger; called with grid page/sort + `additionalData`. */
  fetchPage?: FetchPromptPage;
  additionalData?: Record<string, unknown>;
  selectedId: string | null;
  runningId: string | null;
  runningIds?: string[];
  showTopic: boolean;
  loading?: boolean;
  height?: string;
  onSelect: (id: string) => void;
  onSelectionChange: (ids: Set<string>) => void;
  onRun: (id: string) => void;
  onStop: () => void;
}) {
  const pageRef = useRef<number | null>(null);
  const runningIdSet = useMemo(
    () => new Set([...(runningIds ?? []), ...(runningId ? [runningId] : [])]),
    [runningId, runningIds],
  );

  const fetchFunction = useMemo(
    () => (fetchPage ? toGridFetchFunction(fetchPage) : undefined),
    [fetchPage],
  );

  const columns = useMemo(
    () =>
      promptsColumnConfig({
        selectedId,
        runningIdSet,
        showTopic,
        onSelect,
        onRun,
        onStop,
      }),
    [selectedId, runningIdSet, showTopic, onSelect, onRun, onStop],
  );

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<AgGridRowData>) => {
      const ids = event.api.getSelectedRows().map((row) => String(row.id));
      onSelectionChange(new Set(ids));
    },
    [onSelectionChange],
  );

  const onPaginationChanged = useCallback((event: PaginationChangedEvent<AgGridRowData>) => {
    const page = event.api.paginationGetCurrentPage();
    if (pageRef.current !== null && (pageRef.current !== page || event.newPageSize)) {
      event.api.deselectAll();
    }
    pageRef.current = page;
  }, []);

  const gridOptions = useMemo(
    () => ({
      getRowId: (params: { data: AgGridRowData }) => String(params.data.id),
      rowSelection: {
        mode: 'multiRow' as const,
        selectAll: 'currentPage' as const,
        enableClickSelection: false,
      },
      onSelectionChanged,
      onPaginationChanged,
    }),
    [onSelectionChanged, onPaginationChanged],
  );

  if (loading) {
    return <AgGridSkeleton height={height} />;
  }

  if (prompts.length === 0) {
    return (
      <div className="py-16 text-center small text-body-tertiary">
        هیچ پرامپتی با فیلترها مطابقت ندارد.
      </div>
    );
  }

  return (
    <TypedAgGridWrapper
      table_name="tracker_ai_prompts"
      height={height}
      columns={columns}
      data={prompts}
      total={total ?? prompts.length}
      fetchFunction={fetchFunction}
      additionalData={additionalData}
      settings={{ pagination: true, fullscreen: false, sideBar: false, export: { enabled: false } }}
      otherOptions={gridOptions}
    />
  );
}
